import { Box } from '@wix/design-system';
import { ActionButton, MarketingItem } from '@wix/package-picker-components';
import { withExperiments } from '@wix/wix-experiments-react';
import classNames from 'classnames';
import { CardsContainer } from 'components/SunriseApp/CardsContainer';
import { observer } from 'mobx-react';
import { withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { useProductsGridRefContext } from 'providers/ProductsGridContextRefProvider/ProductsGridContextRefProvider';
import * as React from 'react';
import { isValidElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PlansPageFooter } from '../../../pages/PlansPage/Footer/PlansPageFooter';
import { LimitedTimeBanner } from '../../../pages/PlansPage/LimitedTimeBanner/LimitedTimeBanner';
import { NoTabsBanner } from '../../../pages/PlansPage/PaymentBanners';
import { usePlansPageController } from '../../../pages/PlansPage/PlansPage.controller';
import { PlansPageProps } from '../../../pages/PlansPage/PlansPageProps';
import { useGetProducts } from '../../../pages/PlansPage/shared/useGetProducts';
import { withGtmLogger } from '../../HOCs/withGtmLogger';
import { EnterpriseBanner } from '../EnterpriseBanner';
import { useSunriseProductCards } from '../hooks/useSunriseProductCards';
import { PlansGrid } from '../PlansGrid';
import s from './PlansPage.scss';
import { Experiments, PaymentCycle } from 'enums';

const disabledMonthlyFeatureKeys = [
  'premium-purchase-plan.cards.feature.free_domain',
  'premium-purchase-plan.cards.feature.dropshipping.business_elite',
  'premium-purchase-plan.cards.feature.dropshipping.business',
  'premium-purchase-plan.cards.feature.dropshipping.core',
];

const PlansPageComp: React.FC<PlansPageProps> = (props) => {
  const { appStore } = props.rootStore;
  const {
    appContext: {
      sections,
      featureGroups,
      biLogger,
      locale,
      userGuid,
      clientId,
      experiments,
    },
    shouldStoreIntentNoTabs,
    jumpToTab,
  } = appStore;
  const _sections = sections.filter(
    (section) => section?.displayInDesktop !== false,
  );
  const { t } = useTranslation();
  const { shouldShowLimitedTimeBanner, onSelectProduct } =
    usePlansPageController(props);
  const { scrollToRef } = useProductsGridRefContext();
  const { products } = useGetProducts({
    appStore,
  });
  const { theme } = usePageModeContext();
  const productsCardsData = useSunriseProductCards({
    baseProducts: products,
    appStore,
    clickSource: 'cards',
    onSelectProduct: (product) => onSelectProduct(product, 'cards'),
  });

  const isCycleSelectorsInProductsPageEnabled =
    appStore.isCycleSelectorsInProductsPageEnabled();

  const shouldDisableFeature = React.useCallback(
    (feature: MarketingItem) =>
      isCycleSelectorsInProductsPageEnabled &&
      appStore.currentCycle === PaymentCycle.MONTHLY &&
      isValidElement(feature.text) &&
      disabledMonthlyFeatureKeys.includes(feature.text.props?.i18nKey),
    [appStore.currentCycle],
  );

  const onComparePlanFeaturesClick = () => {
    biLogger.logComparePlanFeaturesCTA({});
    scrollToRef();
  };

  return (
    <div
      data-testid='advanced-plans-page'
      className={classNames(props.className, s.root, theme)}
    >
      <CardsContainer
        productsCardData={productsCardsData}
        shouldDisableFeature={shouldDisableFeature}
        enableAIFeatures={experiments[Experiments.EnableAIToolsInProductCards] === 'true'}
      />
      {shouldShowLimitedTimeBanner && <LimitedTimeBanner />}
      {shouldStoreIntentNoTabs && <NoTabsBanner action={jumpToTab} />}
      <Box margin='SP3 0 SP6 0'>
        <ActionButton onClick={onComparePlanFeaturesClick}>
          {t('premium-purchase-plan.cards.compare_features.cta')}
        </ActionButton>
      </Box>
      <EnterpriseBanner
        locale={locale}
        userGuid={userGuid}
        clientId={clientId}
        isReplaceChangePlanPurchaseModeEnabled={
          experiments[Experiments.ReplaceChangePlanPurchaseMode] === 'true'
        }
        isUnifiedChangePlanPhase1Enabled={
          experiments[Experiments.UnifiedChangePlanPhase1] === 'true'
        }
      />
      <PlansPageFooter />
      <Box marginTop='SP4' width='100%'>
        <PlansGrid
          onClickSelect={(product) => onSelectProduct(product, 'table')}
          features={[]}
          products={products}
          sections={_sections}
          groups={featureGroups ?? []}
        />
      </Box>
    </div>
  );
};

export const PlansPage = withGtmLogger(
  withExperiments(withInjectedRootStore(observer(PlansPageComp))),
);
